// app/src/utils/tokenValidator.ts

import { jwtDecode } from "jwt-decode"
import type { TokenUserPayloadSerializer } from "@/types/member"
import { DEMO_AUTH_TOKEN_KEY, DEMO_MODE_KEY, getStoredWebSessionUser, isWebSessionAuthMode } from "@core/utils/webSessionAuth"

export interface TokenDebugInfo {
  hasToken: boolean
  isValid: boolean
  exp?: number
  expIso?: string
  iat?: number
  iatIso?: string
  remainingMs?: number
}

const getStoredDemoAuthToken = () => {
  if (typeof window === "undefined") {
    return null
  }

  return localStorage.getItem(DEMO_AUTH_TOKEN_KEY)
}

const isDemoMode = () => {
  if (typeof window === "undefined") {
    return false
  }

  return localStorage.getItem(DEMO_MODE_KEY) === "true"
}

const isSessionUserValid = (user: TokenUserPayloadSerializer | null): user is TokenUserPayloadSerializer => {
  // 웹 세션은 서버 세션이 기준이므로 로컬 exp를 만료 판정의 단일 기준으로 쓰지 않는다.
  if (!user || !isWebSessionAuthMode()) {
    return false
  }

  if (typeof user.memberUuid !== "string") {
    return false
  }

  return user.memberUuid.trim().length > 0
}

const toIsoString = (unixSeconds?: number) => {
  if (typeof unixSeconds !== "number" || !Number.isFinite(unixSeconds)) {
    return undefined
  }

  return new Date(unixSeconds * 1000).toISOString()
}

/**
 * JWT 토큰 유효성 검증
 * @param token - JWT 토큰 문자열
 * @returns 토큰이 유효하면 true, 아니면 false
 */
export const isTokenValid = (token: string | null): boolean => {
  if (!token) {
    return false
  }

  try {
    const decoded: any = jwtDecode(token)
    const tokenValid = decoded.exp * 1000 >= Date.now()
    return tokenValid
  } catch (error) {
    console.error("토큰 검증 실패:", error)
    return false
  }
}

/**
 * 저장된 로그인 세션 또는 데모 인증의 유효성을 검증
 * @returns 토큰이 유효하면 true, 아니면 false
 */
export const hasValidAuthSession = (): boolean => {
  const storedSessionUser = getStoredWebSessionUser()
  if (storedSessionUser) {
    return isSessionUserValid(storedSessionUser)
  }

  if (!isDemoMode()) {
    return false
  }

  return isTokenValid(getStoredDemoAuthToken())
}

/**
 * 로그인 상태 확인
 * @returns 로그인 상태이면 true, 아니면 false
 */
export const isLoggedIn = (): boolean => {
  return hasValidAuthSession()
}

export const getTokenDebugInfo = (token?: string | null): TokenDebugInfo => {
  const storedSessionUser = token ? null : getStoredWebSessionUser()
  if (storedSessionUser) {
    const exp = typeof storedSessionUser.exp === "number" ? storedSessionUser.exp : undefined

    return {
      hasToken: true,
      isValid: isSessionUserValid(storedSessionUser),
      exp,
      expIso: toIsoString(exp),
      remainingMs: typeof exp === "number" ? exp * 1000 - Date.now() : undefined,
    }
  }

  const demoAuthToken = token ?? (isDemoMode() ? getStoredDemoAuthToken() : null)

  if (!demoAuthToken) {
    return {
      hasToken: false,
      isValid: false,
    }
  }

  const decoded = decodeToken<any>(demoAuthToken)
  if (!decoded) {
    return {
      hasToken: true,
      isValid: false,
    }
  }

  const exp = typeof decoded.exp === "number" ? decoded.exp : undefined
  const iat = typeof decoded.iat === "number" ? decoded.iat : undefined

  return {
    hasToken: true,
    isValid: isTokenValid(demoAuthToken),
    exp,
    expIso: toIsoString(exp),
    iat,
    iatIso: toIsoString(iat),
    remainingMs: typeof exp === "number" ? exp * 1000 - Date.now() : undefined,
  }
}

/**
 * JWT 토큰에서 사용자 정보 추출
 * @param token - JWT 토큰 문자열
 * @returns 디코딩된 사용자 정보 또는 null
 */
export const decodeToken = <T = any>(token: string | null): T | null => {
  if (!token) {
    return null
  }

  try {
    const decoded = jwtDecode<T>(token)
    return decoded
  } catch (error) {
    console.error("토큰 디코딩 실패:", error)
    return null
  }
}

/**
 * 저장된 로그인 세션 또는 데모 JWT에서 사용자 정보 추출
 */
export const getUserInfoFromToken = (token?: string | null): TokenUserPayloadSerializer | null => {
  const storedSessionUser = getStoredWebSessionUser()
  if (storedSessionUser) {
    return isSessionUserValid(storedSessionUser) ? storedSessionUser : null
  }

  if (!isDemoMode()) {
    return null
  }

  const demoAuthToken = token ?? getStoredDemoAuthToken()

  if (!isTokenValid(demoAuthToken)) {
    return null
  }

  const decoded = decodeToken<any>(demoAuthToken)

  if (!decoded) {
    return null
  }

  try {
    return {
      exp: decoded.exp,
      memberUuid: decoded.member_uuid,
      isActive: decoded.is_active,
      isSuperuser: decoded.is_superuser,
      memberGrant: decoded.member_grant,
      memberName: decoded.member_name,
      memberProfileName: decoded.member_profile_name,
      memberLicenseType: decoded.member_license_type,
      memberLicenseNo: decoded.member_license_no,
    }
  } catch (error) {
    console.error("사용자 정보 추출 실패:", error)
    return null
  }
}

